import { ACC, ACC_L, DARK, DARK_L, METAL_D, METAL_L, ModelBuilder, RED, type WeaponPartName } from './builder';
import type { DesignMeta, V3 } from './designs';

/**
 * Voxel models of the fitted sights ("scopes", see ../scopes.ts), mounted on top of a weapon's own model.
 *
 * Sights are built on a FINER grid than the gun: `ATTACH_RES` = 4 cells per gun voxel (quarter-size voxels, 0.6 cm). A frame wall
 * is then a quarter as thick as the gun's own slide and a whole sight is a small accessory instead of a block as big as the gun.
 * All sizes below are in those cells (4 cells = 1 gun voxel = 2.5 cm).
 * The sight lives in its own `AttachGrid` (rendered as one more mesh on the weapon) and the gun's grid is left alone, apart from
 * what the sight replaces: the built-in optic, iron sights and carry handle standing on the receiver.
 *
 * A sight sits on a thin ribbed rail; short pillars under the rail fill any gap down to the receiver, so it never floats.
 *
 * "Window" sights (the dot sights and the 2x scope) are thin frames around an OPEN window, centered exactly on the sight line:
 * aiming puts the eye behind the window, so the player looks through the model itself and the reticle sits inside it.
 * "Tube" scopes (3x, Marksman, Long-Range) are solid; aiming through them shows the scope mask instead (see ViewModel).
 *
 * Every model returns the sight point the view model aims through (gun voxel coordinates).
 */

/** Cells per gun voxel along each axis in an attachment grid. */
export const ATTACH_RES = 4;

/** A finer-resolution voxel layer drawn on top of a weapon model (sights). */
export interface AttachGrid {
  grid: Uint8Array;
  /** Dimensions in attachment cells (same x + z * sx + y * sx * sz layout as the weapon grid). */
  sx: number;
  sy: number;
  sz: number;
  /** Cells per gun voxel (cell size = gun voxel / res). */
  res: number;
  /** Position of cell (0, 0, 0) in gun voxel coordinates (integers, so a cell block lines up with gun voxels). */
  origin: V3;
}

export interface ScopeBase {
  sx: number;
  sy: number;
  sz: number;
  grid: Uint8Array;
  parts: Map<WeaponPartName, Uint8Array>;
  /** Sight gear voxels in the main grid (tagged by the design); empty when the design does not tag its sights. */
  gear: Uint8Array;
  /** The design's fine hardware layer, if any. */
  detail: { grid: Uint8Array; parts: Map<WeaponPartName, Uint8Array>; gear: Uint8Array } | null;
  meta: DesignMeta;
}

/** How the view model treats the fitted optic while aiming. */
export interface OpticInfo {
  /** The model has an open window on the sight line: the eye aligns with it and the gun is not lowered out of the way. */
  seeThrough: boolean;
  /**
   * Extra eye-to-sight distance in meters while aiming (eye relief: a little more room shows more of the world around the frame).
   * A FIXED distance on purpose: a percentage pushed pistols (already held far out) a third of a meter away from the player.
   */
  relief: number;
}

/** Largest eye relief any sight may use (meters); a test guards it so no sight can push a gun away from the player. */
export const MAX_EYE_RELIEF = 0.06;

export interface ScopeMount {
  /** The weapon grid with the replaced sights removed (same dimensions as the base). */
  grid: Uint8Array;
  parts: Map<WeaponPartName, Uint8Array>;
  /** The fine hardware layer with its sight gear removed (null: the design has none). */
  detail: { grid: Uint8Array; parts: Map<WeaponPartName, Uint8Array>; gear: Uint8Array } | null;
  attach: AttachGrid;
  sight: V3;
  optic: OpticInfo;
}

interface Look extends OpticInfo {
  /** Length along the barrel in attachment cells: [large guns, small guns]. */
  len: [number, number];
  /** Draws the sight with its plate on row `base` (the rail is row `base - 1`); returns the sight-line height (cells). */
  draw(b: ModelBuilder, base: number, z0: number, z1: number, small: boolean): number;
}

/**
 * Open frame sight: base plate, two thin side walls and a top bar around a window `2 * half` cells wide and `h` tall. Only the
 * FRONT rim carries the trim color; the rear rim (the one nearest the eye) stays dark so it never fills the view.
 * Returns the height of the window center (an integer for an even `h`, so the sight line runs on a cell boundary).
 */
function frameSight(b: ModelBuilder, base: number, z0: number, z1: number, half: number, h: number, trim: number, top: number): number {
  const cx = Math.round(b.cx);
  const x0 = cx - half - 1;
  const x1 = cx + half + 1;
  b.box(x0, base, z0, x1, base + 1, z1, METAL_D);
  b.box(x0, base + 1, z0, x0 + 1, base + 1 + h, z1, DARK).box(x1 - 1, base + 1, z0, x1, base + 1 + h, z1, DARK);
  b.box(x0, base + 1 + h, z0, x1, base + 2 + h, z1, top);
  // Front rim trim.
  b.box(x0, base + 1, z0, x0 + 1, base + 2 + h, z0 + 1, trim).box(x1 - 1, base + 1, z0, x1, base + 2 + h, z0 + 1, trim);
  b.box(x0, base + 1 + h, z0, x1, base + 2 + h, z0 + 1, trim);
  return base + 1 + h / 2;
}

/** Hollow tube along z centered at an integer height; the hole is the same size over the whole length (a clear window). */
function openTube(b: ModelBuilder, cy: number, z0: number, z1: number, r: number, hole: number, color: number): void {
  b.tube(b.cx, cy, z0, z1, r, color, hole);
}

const LOOKS: Record<string, Look> = {
  // Red Dot: a small open reflex frame (window 8 x 6 cells = 5 x 3.75 cm) with a red-trimmed front rim.
  red_dot: {
    len: [12, 9],
    seeThrough: true,
    relief: 0.04,
    draw: (b, base, z0, z1, small) => frameSight(b, base, z0, z1, small ? 3 : 4, small ? 4 : 6, RED, DARK),
  },
  // Ring Dot: a round holographic ring with a gold rim.
  ring_dot: {
    len: [10, 8],
    seeThrough: true,
    relief: 0.04,
    draw(b, base, z0, z1, small) {
      const r = small ? 3.6 : 4.5;
      const cy = base + 1 + Math.ceil(r);
      const cx = Math.round(b.cx);
      b.box(cx - 2, base, z0 + 2, cx + 2, base + 1, z1 - 2, METAL_D);
      openTube(b, cy, z0, z1, r, r - 1.3, DARK);
      openTube(b, cy, z0, z0 + 1, r, r - 1.3, ACC);
      openTube(b, cy, z1 - 1, z1, r, r - 1.3, ACC);
      return cy;
    },
  },
  // Chevron Sight: a low frame with a gold top bar and a red arrow-tip block on the front.
  chevron: {
    len: [12, 9],
    seeThrough: true,
    relief: 0.04,
    draw(b, base, z0, z1, small) {
      const h = small ? 4 : 6;
      const axis = frameSight(b, base, z0, z1, small ? 3 : 4, h, ACC_L, ACC);
      const cx = Math.round(b.cx);
      b.box(cx - 2, base + h + 2, z0, cx + 2, base + h + 3, z0 + 4, RED);
      return axis;
    },
  },
  // 2x Scope: a short open tube (an ACOG-style combat scope) with a bell at each end.
  scope_2x: {
    len: [28, 20],
    seeThrough: true,
    relief: 0.03,
    draw(b, base, z0, z1, small) {
      const r = small ? 3.6 : 4.4;
      const cy = base + 1 + Math.ceil(r);
      const hole = r - 1.3;
      const cx = Math.round(b.cx);
      b.box(cx - 2, base, z0 + 5, cx + 2, base + 1, z1 - 4, METAL_D);
      openTube(b, cy, z0 + 6, z1 - 6, r, hole, DARK);
      openTube(b, cy, z0, z0 + 6, r + 1, hole, DARK);
      openTube(b, cy, z0, z0 + 1, r + 1, hole, METAL_L);
      openTube(b, cy, z1 - 6, z1, r + 0.5, hole, DARK_L);
      for (const rz of [z0 + 9, z1 - 11]) {
        openTube(b, cy, rz, rz + 1, r + 0.6, hole, ACC);
        b.box(cx - 1, base, rz, cx + 1, cy - Math.ceil(r), rz + 1, ACC);
      }
      return cy;
    },
  },
  // Solid scopes: the aimed view is the scope mask (the gun hides), so the tube is a closed model with lenses.
  scope_3x: { len: [30, 30], seeThrough: false, relief: 0, draw: (b, base, z0, z1) => solidScope(b, base, z0, z1, 3.8, ACC) },
  marksman: { len: [36, 36], seeThrough: false, relief: 0, draw: (b, base, z0, z1) => solidScope(b, base, z0, z1, 3.3, METAL_L) },
  long_range: { len: [44, 44], seeThrough: false, relief: 0, draw: (b, base, z0, z1) => solidScope(b, base, z0, z1, 4.6, ACC) },
};

/** Closed tube scope with lenses on a thin mount, on the rail. */
function solidScope(b: ModelBuilder, base: number, z0: number, z1: number, r: number, ring: number): number {
  const cy = base + 1 + Math.ceil(r);
  b.scope(cy, z0, z1, r, base, ring);
  const cx = Math.round(b.cx);
  b.box(cx - 2, base, z0 + 5, cx + 2, base + 1, z1 - 4, METAL_D);
  return cy;
}

/** Whether a scope id has a 3D model (the factory sights do not: they are the weapon's own). */
export function hasScopeModel(scopeId: string): boolean {
  return scopeId in LOOKS;
}

export const SCOPE_MODEL_IDS: readonly string[] = Object.keys(LOOKS);

/** Whether the model of `scopeId` has an open window on the sight line. */
export function scopeSeesThrough(scopeId: string): boolean {
  return LOOKS[scopeId]?.seeThrough ?? false;
}

/** Tallest attachment grid (cells): pillars + rail + plate + the biggest sight, with room to spare. Trimmed after drawing. */
const MAX_ATTACH_Y = 64;

/**
 * Mounts scope `scopeId` on a base weapon model: removes what stands on top of the receiver (iron sights, carry handle, the
 * built-in optic), and builds the sight - rail, pillars and the sight itself - as a finer-resolution attachment grid.
 */
export function mountScope(base: ScopeBase, scopeId: string): ScopeMount | null {
  const look = LOOKS[scopeId];
  if (!look) return null;
  const { sx, sy, sz, meta } = base;
  const g = new ModelBuilder(sx, sy, sz);
  g.grid.set(base.grid);
  const parts = new Map<WeaponPartName, Uint8Array>();
  for (const [name, pg] of base.parts) {
    const copy = pg.slice();
    parts.set(name, copy);
    g.parts.set(name, copy);
  }

  const cx = Math.round(sx / 2);
  const small = sx <= 4 || sy <= 14;
  const res = ATTACH_RES;
  const z1 = Math.min(sz, Math.round(meta.mount?.z1 ?? meta.sight[2] + 2));
  const lenCells = small ? look.len[1] : look.len[0];
  const z1c = z1 * res;
  const z0c = Math.max(res, z1c - lenCells);
  const zA = Math.floor(z0c / res);
  const zB = Math.ceil(z1c / res);
  const xa = Math.max(0, cx - 3);
  const xb = Math.min(sx, cx + 3);

  // 0) Sight gear the design tagged (iron sights, handle, built-in optic) is exactly what a scope replaces: remove it everywhere,
  //    including the fine layer. Designs that tag nothing fall back to the geometric rules below.
  const tagged = base.gear.some((v) => v) || !!base.detail?.gear.some((v) => v);
  let detail: ScopeMount['detail'] = null;
  if (base.detail) {
    const dg = base.detail.grid.slice();
    const dparts = new Map<WeaponPartName, Uint8Array>();
    for (const [name, pg] of base.detail.parts) dparts.set(name, pg.slice());
    for (let i = 0; i < dg.length; i++)
      if (base.detail.gear[i]) {
        dg[i] = 0;
        for (const pg of dparts.values()) pg[i] = 0;
      }
    detail = { grid: dg, parts: dparts, gear: new Uint8Array(dg.length) };
  }
  if (tagged)
    for (let i = 0; i < g.grid.length; i++)
      if (base.gear[i]) {
        g.grid[i] = 0;
        for (const pg of parts.values()) pg[i] = 0;
      }

  // 1) The weapon's built-in optic goes first (untagged designs only).
  const st = tagged ? undefined : meta.stock;
  if (st) {
    for (let y = st.y0; y < sy; y++)
      for (let z = st.z0; z < Math.min(sz, st.z1); z++)
        for (let x = 0; x < sx; x++) {
          const v = g.grid[g.index(x, y, z)];
          if (v && (!st.only || st.only.includes(v))) g.set(x, y, z, 0);
        }
  }

  // 2) The receiver line: the height most central columns under the sight top out at (a design can state it with `mount`).
  const colTop = (x: number, z: number): number => {
    for (let y = sy - 1; y >= 0; y--) if (g.grid[g.index(x, y, z)]) return y;
    return -1;
  };
  const centerTop = (z: number): number => Math.max(colTop(cx - 1, z), colTop(cx, z));
  let receiver = meta.mount?.y ?? -1;
  if (receiver < 0) {
    const votes = new Map<number, number>();
    for (let z = zA; z < zB; z++) {
      const t = centerTop(z);
      if (t >= 0) votes.set(t, (votes.get(t) ?? 0) + 1);
    }
    let best = 0;
    for (const [h, n] of votes)
      if (n > best || (n === best && h < receiver)) {
        receiver = h;
        best = n;
      }
  }
  if (receiver < 0) return null;

  // 3) Anything standing above the receiver under the sight (iron sights, handles, ears) is removed (untagged designs only).
  if (!tagged) for (let z = Math.max(0, zA - 1); z < Math.min(sz, zB + 1); z++)
    for (let x = xa; x < xb; x++) for (let y = receiver + 1; y < sy; y++) if (g.grid[g.index(x, y, z)]) g.set(x, y, z, 0);

  // 4) The attachment grid starts on the lowest solid column top under the sight (pillars rise from each column to the rail).
  let lowTop = receiver;
  for (let z = zA; z < zB; z++) {
    const t = centerTop(z);
    if (t >= receiver - 2 && t < lowTop) lowTop = t;
  }
  const origin: V3 = [0, lowTop + 1, 0];
  const mx = sx * res;
  const mz = sz * res;
  const b = new ModelBuilder(mx, MAX_ATTACH_Y, mz);
  const mc = Math.round(b.cx);
  const railY = (receiver + 1 - origin[1]) * res;
  // Pillars from each column's top up to the rail (4 cells = 1 gun voxel wide), then the rail itself (alternating shades, 6 wide).
  for (let z = z0c; z < z1c; z++) {
    const t = centerTop(Math.floor(z / res));
    if (t >= receiver - 2) for (let y = (t + 1 - origin[1]) * res; y < railY; y++) b.box(mc - 2, y, z, mc + 2, y + 1, z + 1, DARK);
    b.box(mc - 3, railY, z, mc + 3, railY + 1, z + 1, (z >> 1) % 2 ? DARK : DARK_L);
  }
  const axis = look.draw(b, railY + 1, z0c, z1c, small);
  b.bevel();

  // Trim the grid to what was drawn.
  let top = 0;
  for (let y = MAX_ATTACH_Y - 1; y >= 0 && top === 0; y--) {
    for (let i = y * mx * mz; i < (y + 1) * mx * mz; i++)
      if (b.grid[i]) {
        top = y + 1;
        break;
      }
  }
  const attach: AttachGrid = { grid: b.grid.slice(0, mx * mz * top), sx: mx, sy: top, sz: mz, res, origin };
  const sight: V3 = [meta.sight[0], origin[1] + axis / res, (z1c - res) / res];
  return { grid: g.grid, parts, detail, attach, sight, optic: { seeThrough: look.seeThrough, relief: look.relief } };
}
